import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { updateClient } from 'services/clients.svc'
import { useFileUploadAndSave } from 'hooks/useFileUploadAndSave'
import { FilePicker } from 'components'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { validateClient } from '@paramount-prestart/shared'
import { deleteFileIfItExists } from 'common/functions'
import { toast } from 'react-toastify'
import {
  IconBuilding,
  IconId,
  IconHammer,
  IconPhone,
  IconMail,
  IconMapPin,
  IconMailbox,
  IconDeviceFloppy,
  IconPhoto,
} from '@tabler/icons-react'
import {
  PLACEHOLDER_ABN,
  PLACEHOLDER_BUILDER_REG_NUMBER,
  PLACEHOLDER_COMPANY_NAME,
  PLACEHOLDER_EMAIL,
  PLACEHOLDER_ONE_LINE_ADDRESS,
  PLACEHOLDER_PHONE,
} from '@paramount-prestart/shared/placeholders'

const EditClientPage = ({ clientDetail, companyId }) => {
  const queryClient = useQueryClient()
  const [values, setValues] = useState(clientDetail)
  const [selectedFile, setSelectedFile] = useState(undefined)
  const [touched, setTouched] = useState({})
  const [submitted, setSubmitted] = useState(false)
  const navigate = useNavigate()
  const errors = useMemo(() => validateClient(values), [values])
  const canSubmit = useMemo(() => {
    const hasNulls = Object.keys(values).some(
      (x) => x !== 'image' && values[x] === null,
    )
    const hasErrors = Object.keys(errors || {}).length > 0
    return !(hasNulls || hasErrors)
  }, [errors])

  const showError = (field) => (touched[field] || submitted) && errors?.[field]
  console.log('[EditClientPage] submitted:', submitted, 'touched:', touched, 'errors:', errors, 'values:', values)

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setValues({ ...values, [name]: value })
  }

  const handleBlur = (e) => {
    setTouched((prev) => ({ ...prev, [e.target.name]: true }))
  }

  const onFileSelected = ({ event, guid }) => {
    if ((!event?.target?.files?.length && !selectedFile) || !event) {
      setSelectedFile(undefined)
      setValues({ ...values, image: null })
    } else if (event?.target?.files?.length) {
      const fileData = event.target.files[0]
      const file = {
        friendlyName: fileData.name,
        fileKey: `${guid}.${fileData.name.split('.').pop()}`,
        type: 'companyProfile',
      }
      setValues({ ...values, image: file })
      setSelectedFile(fileData)
    }
  }

  const editClientMutate = useMutation(() => updateClient(companyId, values), {
    onSuccess: async (data) => {
      if (data?.fileDeleteSASToken) {
        await deleteFileIfItExists(data.fileDeleteSASToken)
      }
      queryClient.setQueryData(['editCompany', companyId], data)
      toast.success('Information updated sucessfully')
      navigate(`/company/${companyId}`)
    },
    onError: async (err) => {
      toast.error(err.message)
      console.error('Failed to update', err)
    },
  })

  const saveMutation = useFileUploadAndSave(selectedFile, values.image, () =>
    editClientMutate.mutate(),
  )

  return (
    <div className="ec-card">
      {/* ── Card header ──────────────────────────────────────────────── */}
      <div className="ec-card-header">
        <span className="ec-card-title">
          <IconBuilding size={15} stroke={1.5} />
          Company Information
        </span>
      </div>

      <div className="ec-form-layout">
        {/* ── Left: tabular field grid ─────────────────────────────── */}
        <div className="ec-fields-grid">

          <div className="ec-cell">
            <label className="ec-cell-label" htmlFor="ec-name">
              <IconBuilding size={11} stroke={1.5} />
              Company Name
              <span className="ec-cell-required" aria-hidden="true">*</span>
            </label>
            <input
              id="ec-name" type="text" name="name"
              className="ec-cell-input"
              value={values.name || ''}
              aria-invalid={showError('name') ? true : undefined}
              placeholder={PLACEHOLDER_COMPANY_NAME}
              onChange={handleInputChange}
              onBlur={handleBlur}
            />
            {showError('name') && <span className="ec-cell-error">{errors.name}</span>}
          </div>

          <div className="ec-cell">
            <label className="ec-cell-label" htmlFor="ec-abn">
              <IconId size={11} stroke={1.5} />
              ABN
              <span className="ec-cell-required" aria-hidden="true">*</span>
            </label>
            <input
              id="ec-abn" type="text" name="abn"
              className="ec-cell-input"
              value={values.abn || ''}
              aria-invalid={showError('abn') ? true : undefined}
              placeholder={PLACEHOLDER_ABN}
              onChange={handleInputChange}
              onBlur={handleBlur}
            />
            {showError('abn') && <span className="ec-cell-error">{errors.abn}</span>}
          </div>

          <div className="ec-cell">
            <label className="ec-cell-label" htmlFor="ec-brn">
              <IconHammer size={11} stroke={1.5} />
              Builders Reg. No.
              <span className="ec-cell-required" aria-hidden="true">*</span>
            </label>
            <input
              id="ec-brn" type="text" name="buildersRegNumber"
              className="ec-cell-input"
              value={values.buildersRegNumber || ''}
              aria-invalid={showError('buildersRegNumber') ? true : undefined}
              placeholder={PLACEHOLDER_BUILDER_REG_NUMBER}
              onChange={handleInputChange}
              onBlur={handleBlur}
            />
            {showError('buildersRegNumber') && <span className="ec-cell-error">{errors.buildersRegNumber}</span>}
          </div>

          <div className="ec-cell">
            <label className="ec-cell-label" htmlFor="ec-phone">
              <IconPhone size={11} stroke={1.5} />
              Office Phone
              <span className="ec-cell-required" aria-hidden="true">*</span>
            </label>
            <input
              id="ec-phone" type="text" name="phone"
              className="ec-cell-input"
              value={values.phone || ''}
              aria-invalid={showError('phone') ? true : undefined}
              placeholder={PLACEHOLDER_PHONE}
              onChange={handleInputChange}
              onBlur={handleBlur}
            />
            {showError('phone') && <span className="ec-cell-error">{errors.phone}</span>}
          </div>

          <div className="ec-cell">
            <label className="ec-cell-label" htmlFor="ec-email">
              <IconMail size={11} stroke={1.5} />
              Office Email
              <span className="ec-cell-required" aria-hidden="true">*</span>
            </label>
            <input
              id="ec-email" type="text" name="email"
              className="ec-cell-input"
              value={values.email || ''}
              aria-invalid={showError('email') ? true : undefined}
              placeholder={PLACEHOLDER_EMAIL}
              onChange={handleInputChange}
              onBlur={handleBlur}
            />
            {showError('email') && <span className="ec-cell-error">{errors.email}</span>}
          </div>

          <div className="ec-cell">
            <label className="ec-cell-label" htmlFor="ec-address">
              <IconMapPin size={11} stroke={1.5} />
              Office Address
              <span className="ec-cell-required" aria-hidden="true">*</span>
            </label>
            <input
              id="ec-address" type="text" name="address"
              className="ec-cell-input"
              value={values.address || ''}
              aria-invalid={showError('address') ? true : undefined}
              placeholder={PLACEHOLDER_ONE_LINE_ADDRESS}
              onChange={handleInputChange}
              onBlur={handleBlur}
            />
            {showError('address') && <span className="ec-cell-error">{errors.address}</span>}
          </div>

          <div className="ec-cell ec-cell--full">
            <label className="ec-cell-label" htmlFor="ec-postalAddress">
              <IconMailbox size={11} stroke={1.5} />
              Postal Address
              <span className="ec-cell-required" aria-hidden="true">*</span>
            </label>
            <input
              id="ec-postalAddress" type="text" name="postalAddress"
              className="ec-cell-input"
              value={values.postalAddress || ''}
              aria-invalid={showError('postalAddress') ? true : undefined}
              placeholder={PLACEHOLDER_ONE_LINE_ADDRESS}
              onChange={handleInputChange}
              onBlur={handleBlur}
            />
            {showError('postalAddress') && <span className="ec-cell-error">{errors.postalAddress}</span>}
          </div>

        </div>

        {/* ── Right: profile image ─────────────────────────────────── */}
        <div className="ec-image-panel">
          <div className="ec-image-panel-header">
            <IconPhoto size={13} stroke={1.5} />
            Profile Image
          </div>
          <FilePicker
            fileMetadata={values?.image}
            selectedFile={selectedFile}
            onFileSelected={onFileSelected}
            fileType="companyProfile"
            buttonLabel="Choose Image"
            iconColor="var(--font-color)"
          />
        </div>
      </div>

      {/* ── Footer: save button ───────────────────────────────────────── */}
      <div className="ec-card-footer">
        <button
          type="button"
          className={`ec-save-btn${!canSubmit ? ' ec-save-btn--invalid' : ''}`}
          onClick={() => { setSubmitted(true); if (canSubmit) saveMutation.mutate() }}
          disabled={editClientMutate.isLoading}
          aria-busy={editClientMutate.isLoading}
        >
          <IconDeviceFloppy size={15} stroke={1.5} />
          {editClientMutate.isLoading ? 'Please wait…' : 'Save Edits'}
        </button>
      </div>
    </div>
  )
}

export default EditClientPage
